import React, { useState, useEffect } from 'react';
import { Box, Card, CardContent, CardActions, Grid, Typography, Button, Divider, Tab, Tabs } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import FormData from 'form-data';
import { useSnackbar } from 'notistack';
import { useDispatch } from 'react-redux';
import { TabContext, TabPanel } from '@mui/lab';
import MetaFields from '../PositionFields/MetaFields';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Plus as PlusIcon } from '../../../assets/icons/plus';
import { styles } from './styles';
import PermissionChecker from '../../../components/common/PermissionChecker';
import BasicFields from '../PositionFields/BasicFields';
import {
    useViewMetaObjectDefinitionMutation,
    useListMetaObjectDefinitionsMutation,
    useSaveMetaObjectDefinitionMutation,
    useModifyMetaObjectDefinitionActionMutation,
} from '../../../api/demandAttributes';

const views = [
    {
        label: 'Basic Attributes',
        value: 'basic-attributes',
    },
    {
        label: 'Custom Attributes',
        value: 'custom-attributes',
    },
];

const validationSchema = Yup.array().of(
    Yup.object({
        masterCustomAttribute: Yup.object().when('id', {
            is: (value) => !value,
            then: (schema) => schema.required('Master Name is required'),
            otherwise: (schema) => schema.notRequired(),
        }),
        fieldLabel: Yup.string().required('Field Label is required'),
        fieldListOptions: Yup.string().when('fieldDataType', {
            is: (value) => ['Single Selection List', 'Multiple Selection List'].indexOf(value) >= 0,
            then: (schema) => schema.required('Values is required'),
            otherwise: (schema) => schema.notRequired(),
        }),
    }),
);

const AddCustomAttributes = () => {
    const [view, setView] = useState('basic-attributes');
    const [basicRowsData, setBasicRowsData] = useState([]);
    const [deletedFieldIds, setDeletedFieldIds] = useState([]);
    const [masterCustomAttributes, setMasterCustomAttributes] = useState([]);
    const [metaCustomObjectId, setMetaCustomObjectId] = useState(null);
    const [isCreate, setIsCreate] = useState(true);
    const { enqueueSnackbar } = useSnackbar();

    const [viewMetaObjectDefinition] = useViewMetaObjectDefinitionMutation();
    const [saveMetaObjectDefinition, { isLoading: isSaving }] = useSaveMetaObjectDefinitionMutation();
    const [modifyMetaObjectDefinitionAction, { isLoading: isModifying }] =
        useModifyMetaObjectDefinitionActionMutation();

    const isLoading = isSaving || isModifying;

    const formik = useFormik({
        initialValues: [],
        validateOnChange: true,
        validateOnBlur: true,
        validationSchema: validationSchema,
        onSubmit: (values) => handleSubmit(values),
    });

    const getMetaCustomFields = async () => {
        try {
            const formData = new FormData();
            formData.append('metClassName', 'Position');
            const response = await viewMetaObjectDefinition(formData).unwrap();
            if (!response) return;
            if (response?.status === 'ERROR') throw new Error(response?.message);

            setMetaCustomObjectId(response?.metaCustomObjectDefinition?.id || null);
            const metaCustomFieldDefinitions = response?.metaCustomObjectDefinition?.metaCustomFieldDefinitions || [];
            const mappedData = metaCustomFieldDefinitions.map((item) => ({
                id: item.id,
                masterCustomAttribute: item.masterCustomAttribute
                    ? {
                          id: item.masterCustomAttribute?.id,
                          name: item.masterCustomAttribute?.attributeName,
                      }
                    : '',
                fieldLabel: item.fieldLabel,
                fieldDataType: item.fieldDataType,
                fieldListOptions: Array.isArray(item.fieldListOptions) ? item.fieldListOptions?.join(',') : '',
                category: item.category?.name,
                isRequired: item.isRequired,
            }));
            formik.setValues(mappedData);
            setMasterCustomAttributes(response?.masterCustomAttributes || []);
            setBasicRowsData(response?.basicAttributes || []);
            metaCustomFieldDefinitions.length > 0 ? setIsCreate(false) : setIsCreate(true);
        } catch (err) {
            if (err?.statusCode === 400) enqueueSnackbar(err?.data?.message, { variant: 'error' });
            else enqueueSnackbar(err?.message || 'Something went wrong!', { variant: 'error' });
        }
    };

    useEffect(() => {
        getMetaCustomFields();
    }, []);

    const handleAdd = () => {
        const rowsInput = {
            masterCustomAttribute: '',
            fieldLabel: '',
            fieldDataType: 'String',
            fieldListOptions: '',
            category: 'Minor',
            isRequired: false,
        };
        formik.setValues([...formik.values, rowsInput]);
    };

    const handleChange = (index, e) => {
        const { name, value } = e.target;
        const rowsInput = [...formik.values];
        let originalData = rowsInput[index]['original'];
        if (formik.values[index].id && (originalData?.[name] ?? null) === null) {
            rowsInput[index]['original'] = {
                ...originalData,
                [name]: formik.values[index][name],
            };
        }
        rowsInput[index][name] = value;
        rowsInput[index]['isModify'] = true;
        formik.setValues(rowsInput);
    };

    const handleDelete = (index) => {
        const rows = [...formik.values];
        const deletedRow = rows.splice(index, 1);
        setDeletedFieldIds([...deletedFieldIds, deletedRow[0]?.id]);
        formik.setValues(rows);
    };

    const handleSubmit = async (metaData) => {
        try {
            const data = new FormData();
            data.append('baseObjectMetaClassName', 'Position');
            data.append('id', metaCustomObjectId);
            deletedFieldIds.length > 0 && data.append('deletedFieldIds', deletedFieldIds);
            let index = 0;
            metaData.forEach((row) => {
                if (!row.isModify) return;
                if (row.id) data.append(`metaCustomFieldDefinitions[${index}].id`, row.id);
                row.masterCustomAttribute &&
                    data.append(
                        `metaCustomFieldDefinitions[${index}].masterCustomAttribute`,
                        row.masterCustomAttribute?.id || '',
                    );
                data.append(`metaCustomFieldDefinitions[${index}].fieldLabel`, row.fieldLabel);
                data.append(`metaCustomFieldDefinitions[${index}].fieldDataType`, row.fieldDataType);
                if (['Single Selection List', 'Multiple Selection List'].indexOf(row.fieldDataType) >= 0)
                    data.append(`metaCustomFieldDefinitions[${index}].fieldListOptions`, row.fieldListOptions);
                data.append(`metaCustomFieldDefinitions[${index}].category`, row.category || '');
                data.append(`metaCustomFieldDefinitions[${index}].isRequired`, row.isRequired);
                index++;
            });

            if (index === 0 && deletedFieldIds.length === 0) return;

            let response;
            if (!isCreate) {
                response = await modifyMetaObjectDefinitionAction(data).unwrap();
            } else {
                response = await saveMetaObjectDefinition(data).unwrap();
            }

            if (!response) return;
            if (response?.status === 'ERROR') throw new Error(response?.message);
            enqueueSnackbar(response?.message, { variant: 'success' });
            setDeletedFieldIds([]);
            getMetaCustomFields();
        } catch (err) {
            if (err?.statusCode === 400) enqueueSnackbar(err?.data?.message, { variant: 'error' });
            else enqueueSnackbar(err?.message || 'Something went wrong!', { variant: 'error' });
        }
    };

    return (
        <Box>
            <Box sx={styles.contentHeaderWrapper}>
                <Typography variant="h4">Demand Attributes</Typography>
                {view === 'custom-attributes' && (
                    <Box>
                        <PermissionChecker
                            requiredPermissions={[
                                'ROLE_BASE_PERMISSION_INDEX_CREATE_META_OBJECT_DEFN',
                                'ROLE_BASE_PERMISSION_INDEX_UPDATE_META_OBJECT_DEFN',
                            ]}
                            noAuthRedirect={false}
                        >
                            <Button
                                color="primary"
                                onClick={() => handleAdd()}
                                size="medium"
                                startIcon={<PlusIcon fontSize="small" />}
                                variant="contained"
                            >
                                Add
                            </Button>
                        </PermissionChecker>
                    </Box>
                )}
            </Box>
            <Box sx={styles.contentBodyWrapper}>
                <Card>
                    <CardContent>
                        <Grid container direction="column" spacing={2}>
                            <Grid item container justifyContent="space-between" alignItems="center">
                                <Grid item>
                                    <TabContext value={view}>
                                        <Tabs
                                            value={view}
                                            onChange={(event, value) => setView(value)}
                                            aria-label="basic tabs example"
                                        >
                                            {views.map((option) => (
                                                <Tab
                                                    key={option.label}
                                                    disabled={false}
                                                    label={option.label}
                                                    value={option.value}
                                                />
                                            ))}
                                        </Tabs>
                                        <Divider sx={styles.divider} />
                                        <TabPanel
                                            sx={{
                                                ...styles.tabpanel,
                                                px: '0px',
                                                pt: '0px',
                                            }}
                                            value={'basic-attributes'}
                                        >
                                            <BasicFields data={basicRowsData} />
                                        </TabPanel>
                                        <TabPanel
                                            sx={{
                                                ...styles.tabpanel,
                                                px: '0px',
                                                pt: '40px',
                                            }}
                                            value={'custom-attributes'}
                                        >
                                            <MetaFields
                                                formik={formik}
                                                masterCustomAttributes={masterCustomAttributes}
                                                handleChange={handleChange}
                                                handleDelete={handleDelete}
                                            />
                                        </TabPanel>
                                    </TabContext>
                                </Grid>
                            </Grid>
                        </Grid>
                    </CardContent>
                    <Divider />
                    {view === 'custom-attributes' && (
                        <CardActions
                            disableSpacing
                            sx={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                            }}
                        >
                            <PermissionChecker
                                requiredPermissions={[
                                    'ROLE_BASE_PERMISSION_INDEX_CREATE_META_OBJECT_DEFN',
                                    'ROLE_BASE_PERMISSION_INDEX_UPDATE_META_OBJECT_DEFN',
                                ]}
                                noAuthRedirect={false}
                            >
                                <LoadingButton
                                    loading={isLoading}
                                    color="primary"
                                    variant="contained"
                                    size="large"
                                    onClick={formik.handleSubmit}
                                >
                                    {!isCreate ? 'Update' : 'Save'}
                                </LoadingButton>
                            </PermissionChecker>
                        </CardActions>
                    )}
                </Card>
            </Box>
        </Box>
    );
};

export default AddCustomAttributes;
